import { createFileRoute } from '@tanstack/react-router';
import { Brain, Eye, Settings, Users } from 'lucide-react';
import { useState } from 'react';
import { VisionSection } from '@/components/knowledge-base/vision-section';
import { CharacteristicsSection } from '@/components/knowledge-base/characteristics-section';
import { RulesSection } from '@/components/knowledge-base/rules-section';
import { PersonasSection } from '@/components/knowledge-base/personas-section';
import { useGetProject } from '@/api/projects/get-project';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.tsx';
import { useOrganization } from '@/contexts/organization-context';

export const Route = createFileRoute('/_authenticated/knowledge-base/')({
  component: KnowledgeBasePage
});

const sections = [
  {
    key: 'vision',
    label: 'Vision',
    icon: Eye,
    content: <VisionSection />
  },
  {
    key: 'personas',
    label: 'Personas',
    icon: Users,
    content: <PersonasSection />
  },
  {
    key: 'characteristics',
    label: 'Caractéristiques & contraintes',
    icon: Settings,
    content: <CharacteristicsSection />
  },
  {
    key: 'rules',
    label: 'Mémoire et règles',
    icon: Brain,
    content: <RulesSection />
  }
];
function KnowledgeBasePage() {
  const { organizationId, projectId } = useOrganization();
  const { data: project, isLoading } = useGetProject(organizationId!, projectId);
  const [activeSection, setActiveSection] = useState<string>(sections[0].key);
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      history.back();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Chargement de la base de connaissance...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Projet introuvable.</p>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-2xl font-bold">
            <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">
              {project.name}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex h-[calc(90vh-100px)]">
          {/* Menu latéral */}
          <div className="w-64 border-r bg-muted/30 flex flex-col">
            <nav className="flex-1 p-4 space-y-2">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.key}
                    onClick={() => setActiveSection(section.key)}
                    className={`w-full cursor-pointer flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeSection === section.key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{section.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto p-6">{sections.find((s) => s.key === activeSection)?.content}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
